import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Mail, Eye, MousePointerClick, RefreshCw, TrendingUp, Send } from "lucide-react";

interface TrackingStats {
  totalSent: number;
  uniqueOpens: number;
  uniqueClicks: number;
  openRate: number;
  clickRate: number;
  clickToOpenRate: number;
  topTemplates: { name: string; sent: number; opens: number; clicks: number }[];
}

type TimeRange = "7d" | "30d" | "90d";

const RANGE_LABELS: Record<TimeRange, string> = { "7d": "7 days", "30d": "30 days", "90d": "90 days" };

const EmailTrackingCard = () => {
  const [stats, setStats] = useState<TrackingStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState<TimeRange>("30d");

  const load = async () => {
    setLoading(true);

    const daysAgo = range === "7d" ? 7 : range === "30d" ? 30 : 90;
    const since = new Date(Date.now() - daysAgo * 86400000).toISOString();

    // Get all sent emails (deduplicated by message_id, latest status)
    const { data: sentLogs } = await supabase
      .from("email_send_log")
      .select("message_id, template_name")
      .eq("status", "sent")
      .not("message_id", "is", null)
      .gte("created_at", since);

    // Deduplicate by message_id
    const uniqueMessages = new Map<string, string>();
    (sentLogs || []).forEach((log) => {
      if (log.message_id && !uniqueMessages.has(log.message_id)) {
        uniqueMessages.set(log.message_id, log.template_name);
      }
    });

    const totalSent = uniqueMessages.size;

    if (totalSent === 0) {
      setStats({
        totalSent: 0, uniqueOpens: 0, uniqueClicks: 0,
        openRate: 0, clickRate: 0, clickToOpenRate: 0, topTemplates: [],
      });
      setLoading(false);
      return;
    }

    // Get tracking events
    const messageIds = [...uniqueMessages.keys()];
    const { data: trackingEvents } = await supabase
      .from("email_tracking_events")
      .select("message_id, event_type")
      .in("message_id", messageIds.slice(0, 500));

    // Count unique message_ids with opens/clicks
    const openedMids = new Set<string>();
    const clickedMids = new Set<string>();
    (trackingEvents || []).forEach((ev) => {
      if (ev.event_type === "open") openedMids.add(ev.message_id);
      if (ev.event_type === "click") clickedMids.add(ev.message_id);
    });

    const uniqueOpens = openedMids.size;
    const uniqueClicks = clickedMids.size;
    const openRate = totalSent > 0 ? Math.round((uniqueOpens / totalSent) * 100) : 0;
    const clickRate = totalSent > 0 ? Math.round((uniqueClicks / totalSent) * 100) : 0;
    const clickToOpenRate = uniqueOpens > 0 ? Math.round((uniqueClicks / uniqueOpens) * 100) : 0;

    // Per-template stats
    const templateStats = new Map<string, { sent: number; opens: number; clicks: number }>();
    uniqueMessages.forEach((tmpl, mid) => {
      if (!templateStats.has(tmpl)) templateStats.set(tmpl, { sent: 0, opens: 0, clicks: 0 });
      const s = templateStats.get(tmpl)!;
      s.sent++;
      if (openedMids.has(mid)) s.opens++;
      if (clickedMids.has(mid)) s.clicks++;
    });

    const topTemplates = [...templateStats.entries()]
      .map(([name, s]) => ({ name, ...s }))
      .sort((a, b) => b.sent - a.sent)
      .slice(0, 5);

    setStats({ totalSent, uniqueOpens, uniqueClicks, openRate, clickRate, clickToOpenRate, topTemplates });
    setLoading(false);
  };

  useEffect(() => { load(); }, [range]);

  const formatTemplateName = (name: string) =>
    name.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <TooltipProvider delayDuration={200}>
      <Card className="border-border">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp size={15} className="text-primary" />
              Email Engagement Analytics
            </CardTitle>
            <div className="flex items-center gap-1">
              {(["7d", "30d", "90d"] as TimeRange[]).map((r) => (
                <Button
                  key={r}
                  variant={range === r ? "default" : "ghost"}
                  size="sm"
                  className="h-6 px-2 text-[10px]"
                  onClick={() => setRange(r)}
                  disabled={loading}
                >
                  {r}
                </Button>
              ))}
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={load} disabled={loading}>
                <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Open and click tracking — last {RANGE_LABELS[range]}
          </p>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-6">
              <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : !stats || stats.totalSent === 0 ? (
            <p className="text-center text-muted-foreground py-6 text-sm">
              No emails sent yet — tracking data will appear here once emails are delivered.
            </p>
          ) : (
            <div className="space-y-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                {[
                  { label: "Sent", value: stats.totalSent, icon: Send, color: "text-foreground" },
                  { label: "Opened", value: stats.uniqueOpens, icon: Eye, color: "text-green-600" },
                  { label: "Clicked", value: stats.uniqueClicks, icon: MousePointerClick, color: "text-blue-600" },
                  { label: "Open Rate", value: `${stats.openRate}%`, icon: Eye, color: stats.openRate > 30 ? "text-green-600" : "text-muted-foreground" },
                  { label: "Click Rate", value: `${stats.clickRate}%`, icon: MousePointerClick, color: stats.clickRate > 5 ? "text-blue-600" : "text-muted-foreground" },
                  { label: "CTOR", value: `${stats.clickToOpenRate}%`, icon: TrendingUp, color: stats.clickToOpenRate > 15 ? "text-primary" : "text-muted-foreground" },
                ].map((s) => (
                  <Tooltip key={s.label}>
                    <TooltipTrigger asChild>
                      <div className="text-center p-2 rounded-lg bg-muted/50">
                        <s.icon size={14} className={`mx-auto mb-1 ${s.color}`} />
                        <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
                        <p className="text-[10px] text-muted-foreground">{s.label}</p>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="text-xs">
                      {s.label === "CTOR" ? "Click-to-Open Rate: clicks ÷ opens" : s.label}
                    </TooltipContent>
                  </Tooltip>
                ))}
              </div>

              {/* Per-template breakdown */}
              {stats.topTemplates.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2">By Template</p>
                  <div className="space-y-1.5">
                    {stats.topTemplates.map((t) => {
                      const tOpenRate = t.sent > 0 ? Math.round((t.opens / t.sent) * 100) : 0;
                      const tClickRate = t.sent > 0 ? Math.round((t.clicks / t.sent) * 100) : 0;
                      return (
                        <div key={t.name} className="flex items-center justify-between text-xs py-1.5 px-2 rounded bg-muted/30">
                          <span className="font-medium text-foreground truncate max-w-[180px]">
                            {formatTemplateName(t.name)}
                          </span>
                          <div className="flex items-center gap-3 shrink-0">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="flex items-center gap-1 text-muted-foreground">
                                  <Send size={10} /> {t.sent}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="text-xs">Sent</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className={`flex items-center gap-1 ${t.opens > 0 ? "text-green-600" : "text-muted-foreground"}`}>
                                  <Eye size={10} /> {tOpenRate}%
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="text-xs">{t.opens} opened ({tOpenRate}%)</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className={`flex items-center gap-1 ${t.clicks > 0 ? "text-blue-600" : "text-muted-foreground"}`}>
                                  <MousePointerClick size={10} /> {tClickRate}%
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="text-xs">{t.clicks} clicked ({tClickRate}%)</TooltipContent>
                            </Tooltip>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </TooltipProvider>
  );
};

export default EmailTrackingCard;